/**
 * Slice 56 / 59 — the one calm grey line about signal. No button, no alarm, no red.
 * It shows while there is no signal or anything saved is still sending, and goes when everything has sent.
 * `compact` is the one-line version for the jobs list header.
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { useOfflineStatus } from "@/hooks/useOfflineStatus";

export function OfflineLine({ className, compact }: { className?: string; compact?: boolean }) {
  const { t } = useTranslation();
  const { line, progress } = useOfflineStatus();
  if (line === "none") return null;

  const sendingText = progress.total > 1
    ? t("offlineSendingCount").replace("{{done}}", String(progress.done)).replace("{{total}}", String(progress.total))
    : t("offlineSending");
  const text =
    line === "offline"
      ? t(compact ? "offlineBannerShort" : "offlineBanner")
      : line === "weak"
        ? t(compact ? "offlineWeakShort" : "offlineWeak")
        : line === "sending"
          ? sendingText
          : line === "reopened"
            ? t("offlineReopened")
            : t("offlineSent");

  if (line === "sent") {
    return (
      <div className={cn("flex items-center gap-2 rounded-[12px] bg-[#e2eeed] px-3 py-2 text-[13.5px] font-bold text-[#15776d]", className)}>
        <Check className="h-4 w-4" strokeWidth={3} />
        {text}
      </div>
    );
  }
  const showBar = line === "sending" && progress.total > 1;
  return (
    <div className={cn("flex flex-col gap-1.5 rounded-[12px] bg-[#eef2f5] px-3", compact ? "py-1.5" : "py-2", className)} role="status">
      <div className="flex items-center gap-2.5">
        <span className="h-2 w-2 shrink-0 rounded-full bg-[#94a3b0]" />
        <span className={cn("font-medium leading-snug text-[#64748b]", compact ? "text-[12.5px]" : "text-[13.5px]")}>{text}</span>
      </div>
      {showBar ? (
        <div className="h-1 overflow-hidden rounded-full bg-[#dbe3e9]">
          <div className="h-full rounded-full bg-[#94a3b0] transition-all" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
        </div>
      ) : null}
    </div>
  );
}
